// src/components/video/VideoUpload.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "@/utils/axios";
import styles from "./VideoUpload.module.scss";

const LANGS = ["en", "si", "ta", "hi"];

/** Read intrinsic video dimensions before uploading (no network I/O). */
async function sniffVideoMeta(file) {
  return new Promise((resolve) => {
    try {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.muted = true;
      el.playsInline = true;

      el.onloadedmetadata = () => {
        URL.revokeObjectURL(el.src);
        const width = el.videoWidth || 0;
        const height = el.videoHeight || 0;
        const orientation =
          width && height && width < height ? "portrait" : "landscape";
        resolve({ width, height, orientation });
      };

      el.onerror = () => {
        URL.revokeObjectURL(el.src || "");
        resolve({ width: 0, height: 0, orientation: "landscape" });
      };

      el.src = URL.createObjectURL(file);
    } catch {
      resolve({ width: 0, height: 0, orientation: "landscape" });
    }
  });
}

export default function VideoUpload({
  defaultLang = "en",
  onDone, // ({ videoId, jobId })
  pollIntervalMs = 8000,
  initialVideoId = null, // hydrate from an existing video
  showUploader = true, // hide uploader when editing only
}) {
  const [activeLang, setActiveLang] = useState(
    LANGS.includes(defaultLang) ? defaultLang : "en"
  );

  const [videoId, setVideoId] = useState(null);
  const [jobId, setJobId] = useState(null);

  const [status, setStatus] = useState("draft"); // draft|uploading|processing|ready|published|failed
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [file, setFile] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);

  const [meta, setMeta] = useState({
    en: { title: "", subtitle: "" },
    si: { title: "", subtitle: "" },
    ta: { title: "", subtitle: "" },
    hi: { title: "", subtitle: "" },
  });

  const base = useMemo(() => `/v1/videos`, []);
  const uploadToastId = useRef(null);

  // --- helpers
  const normalizeMeta = (m) => {
    const baseFields = { title: "", subtitle: "" };
    const blank = Object.fromEntries(LANGS.map((l) => [l, { ...baseFields }]));
    if (!m || typeof m !== "object") return blank;

    // { en:{title,subtitle}, si:{...}, ... }
    if (m.en || m.si || m.ta || m.hi) {
      const out = { ...blank };
      for (const l of LANGS) if (m[l]) out[l] = { ...baseFields, ...m[l] };
      return out;
    }

    // { title_en, subtitle_en, ... }
    const out = { ...blank };
    for (const l of LANGS) {
      out[l] = {
        title: m[`title_${l}`] || "",
        subtitle: m[`subtitle_${l}`] || "",
      };
    }
    return out;
  };

  async function loadVideoDoc(id) {
    try {
      const { data } = await api.get(`${base}/${id}`, { skipToast: true });

      // Support multiple response shapes
      const doc = (data && (data.video || data.doc || data)) || {};

      // try common places for meta
      const m =
        doc.meta ||
        data?.meta ||
        data?.video?.meta ||
        (Array.isArray(data?.video) ? data.video[0]?.meta : null) ||
        null;

      if (m) setMeta(normalizeMeta(m));

      const st =
        doc.status ||
        data?.status ||
        data?.video?.status ||
        (Array.isArray(data?.video) ? data.video[0]?.status : null);
      if (st) setStatus(st);
    } catch (e) {
      console.error("loadVideoDoc failed", e);
    }
  }

  function setMetaField(lang, field, value) {
    setMeta((m) => ({ ...m, [lang]: { ...m[lang], [field]: value } }));
  }

  // --- handle upload + submit (no thumbnails/subtitles, publish is automatic)
  async function handleUpload() {
    try {
      setMessage("");
      setError("");
      setUploadPct(0);
      if (!file) throw new Error("Pick a video file first");

      const contentType = file.type || "application/octet-stream";
      const filename = file.name || "upload";

      // NEW: sniff client-side meta before presign
      const clientMeta = (await sniffVideoMeta(file).catch(() => null)) || {
        width: 0,
        height: 0,
        orientation: "landscape",
      };

      // Send clientMeta so backend can build portrait/landscape ladder correctly
      const { data: presign } = await api.post(`${base}/presign`, {
        contentType,
        filename,
        clientMeta, // <-- important
      });

      setVideoId(presign.id);
      setJobId(presign.jobId);
      loadVideoDoc(presign.id);

      setStatus("uploading");
      setMessage(`Uploading ${filename} to S3… 0%`);

      uploadToastId.current = toast.loading("Uploading… 0%", {
        closeOnClick: false,
        autoClose: false,
        progress: 0,
      });

      // PUT to S3 with progress
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", presign.url, true);
        xhr.setRequestHeader(
          "Content-Type",
          presign.contentType || contentType
        );

        xhr.upload.onprogress = (e) => {
          if (!e.lengthComputable) return;
          const pct = Math.round((e.loaded / e.total) * 100);
          setUploadPct(pct);
          setMessage(`Uploading… ${pct}%`);
          if (uploadToastId.current) {
            toast.update(uploadToastId.current, {
              render: `Uploading… ${pct}%`,
              progress: pct / 100,
            });
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadPct(100);
            if (uploadToastId.current) {
              toast.update(uploadToastId.current, {
                render: "Upload complete. Processing has started…",
                type: "success",
                isLoading: false,
                autoClose: 3500,
              });
              uploadToastId.current = null;
            }
            resolve();
          } else {
            reject(new Error(`S3 upload failed (HTTP ${xhr.status || "0"})`));
          }
        };

        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.send(file);
      });

      // Save current localization immediately
      await api.post(`${base}/${presign.id}/localize`, { meta });

      setMessage("Uploaded! Submitting for processing…");

      // Submit for processing (MediaConvert)
      await api.post(`${base}/${presign.id}/submit`);

      setStatus("processing");
      setMessage("Processing started. This may take a while.");
      onDone?.({ videoId: presign.id, jobId: presign.jobId });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.message || e?.message || "Upload failed";
      setError(msg);
      setStatus("failed");
      toast.error(msg);
      if (uploadToastId.current) {
        toast.update(uploadToastId.current, {
          render: "Upload failed.",
          type: "error",
          isLoading: false,
          autoClose: 4000,
        });
        uploadToastId.current = null;
      }
    }
  }

  async function saveLocalization() {
    try {
      setError("");
      if (!videoId) {
        toast.info("Upload a video first to save localization to the server.");
        return;
      }
      const { data } = await api.post(`${base}/${videoId}/localize`, { meta });
      const v = data?.video || data || {};
      setMessage("Localization saved.");
      toast.success("Localization saved.");
      if (v.meta) setMeta(normalizeMeta(v.meta));
      else await loadVideoDoc(videoId);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed saving localization";
      setError(msg);
      toast.error(msg);
    }
  }

  // --- hydrate from an existing id (Admin page passes it)
  useEffect(() => {
    if (!initialVideoId) return;
    setVideoId(initialVideoId);
    loadVideoDoc(initialVideoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialVideoId]);

  // --- hydrate when a new id appears (post-presign)
  useEffect(() => {
    if (!videoId) return;
    loadVideoDoc(videoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // --- status polling (auto publish handled on backend)
  useEffect(() => {
    if (!videoId) return;
    let timer = null;
    const tick = async () => {
      try {
        const { data } = await api.get(`${base}/${videoId}`, {
          skipToast: true,
        });
        const doc = data?.video || data || {};
        const st = doc.status || data?.status;
        if (st && st !== status) setStatus(st);
      } catch {
        /* ignore */
      } finally {
        timer = setTimeout(tick, pollIntervalMs);
      }
    };
    timer = setTimeout(tick, pollIntervalMs);
    return () => timer && clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  return (
    <div className={`nu-card ${styles.card}`}>
      <h3 className={styles.h3}>Video Upload</h3>

      {showUploader && (
        <div className={styles.box}>
          <div className={styles.row}>
            <span className={styles.label}>Video file</span>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className={styles.fileInput}
            />
          </div>
          <div className={styles.actions}>
            <button
              onClick={handleUpload}
              disabled={!file}
              className={`nu-btn is-mr ${styles.btn}`}
            >
              Upload &amp; Submit
            </button>
          </div>

          {status === "uploading" && (
            <div className={styles.progressWrap} aria-live="polite">
              <div className={styles.progressTrack}>
                <div
                  className={styles.progressBar}
                  style={{ width: `${uploadPct}%` }}
                />
              </div>
              <div className={styles.progressLabel}>{uploadPct}%</div>
            </div>
          )}
        </div>
      )}

      {/* Localization only */}
      <div className={styles.box}>
        <h4 className={styles.h4}>Localization (title + subtitle)</h4>

        <div className={styles.tabs}>
          {LANGS.map((l) => {
            const active = activeLang === l;
            return (
              <button
                key={l}
                onClick={() => setActiveLang(l)}
                className={[
                  "nu-btn",
                  "nu-btn--sm",
                  active ? "is-mr" : "nu-btn--ghost",
                  styles.tab,
                ].join(" ")}
                aria-pressed={active ? "true" : "false"}
              >
                {l.toUpperCase()}
              </button>
            );
          })}
        </div>

        <div className={styles.inputsGrid}>
          <input
            placeholder={`${activeLang.toUpperCase()} Title`}
            value={meta[activeLang]?.title ?? ""}
            onChange={(e) => setMetaField(activeLang, "title", e.target.value)}
            className={styles.textInput}
          />
        </div>

        <div className={styles.inputsGrid}>
          <input
            placeholder={`${activeLang.toUpperCase()} Subtitle (short line)`}
            value={meta[activeLang]?.subtitle ?? ""}
            onChange={(e) =>
              setMetaField(activeLang, "subtitle", e.target.value)
            }
            className={styles.textInput}
          />
        </div>

        <div className={styles.actions}>
          <button
            onClick={saveLocalization}
            disabled={!videoId}
            title={!videoId ? "Upload a video first to save to server" : ""}
            className={`nu-btn ${styles.btn}`}
          >
            Save Localization
          </button>
        </div>
      </div>

      {/* Status */}
      <div className={`${styles.box} ${styles.statusBox}`}>
        <div className={styles.statusLine}>
          <span>
            Status: <b>{status}</b>
          </span>
        </div>

        {jobId && (
          <div className={styles.mono}>
            jobId: <code>{jobId}</code>
          </div>
        )}
        {videoId && (
          <div className={styles.mono}>
            id: <code>{videoId}</code>
          </div>
        )}

        {message && <div className={styles.message}>{message}</div>}
        {error && <div className={styles.err}>⚠ {error}</div>}
        {/* No Publish button — auto-publish handled by backend */}
      </div>
    </div>
  );
}
